import React from 'react';
import {
    Box,
    Button,
    FormControl,
    FormHelperText,
    Grid,
    LinearProgress,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    TextFieldProps,
    Typography,
    useTheme
} from '@mui/material';
import { useTenantContext } from 'contexts/tenantContext';
import { useTranslation } from 'react-i18next';
import ImageNotSupportedIcon from '@mui/icons-material/ImageNotSupported';
import * as Styles from 'pages/development/settings/Settings.styles';
import { IPropsSettings } from '../Settings';

import * as tenantService from 'services/tenant-service';
import { guestTenant, updateGuestTenant } from 'services/tenant-service';
import { useAuthContext } from 'contexts/authContext';

const ZoneTab = (props: IPropsSettings) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const { checkAuth } = useAuthContext();
    const { tenantId } = props;
    const [selectedTenant, setSelectedTenant] = React.useState<tenantService.guestTenant>({} as tenantService.guestTenant);
    const [email, setEmail] = React.useState<string>('');
    const [tel, setTel] = React.useState<string>('');
    const [adress, setAdress] = React.useState<string>('');
    const [addressOnleline, setAddressOneline] = React.useState<string>('');
    const [emailBescherming, setEmailBescherming] = React.useState<string>('');
    const [defaultLanguage, setDefaultLanguage] = React.useState<string>('');
    const [availableLanguages] = React.useState<{ value: string, displayName: string }[]>([
        { value: 'nl', displayName: 'Nederlands' },
        { value: 'fr', displayName: 'Français' },
        // { value: 'en', displayName: 'English'}
    ]);
    const [logoError, setLogoError] = React.useState<boolean>(false);
    const [backgroundError, setBackgroundError] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);

    React.useEffect(() => {
        setLoading(true);
        setLogoError(true);
        setBackgroundError(true);
        tenantService.get(tenantId).then((res) => {
            setSelectedTenant(res);
            setEmail(res.email);
            setTel(res.tel);
            setAdress(res.address);
            setEmailBescherming(res.emailBescherming);
            setDefaultLanguage(res.defaultLanguage);
            setAddressOneline(res.oneLineAddress);
            setLoading(false);
            setLogoError(false);
            setBackgroundError(false);
        });
    }, [tenantId]);

    const handleUpdate = () => {
        var data : updateGuestTenant = {
            email: email, 
            tel: tel, 
            address: adress, 
            oneLineAddress: addressOnleline, 
            emailBescherming: emailBescherming, 
            defaultLanguage: defaultLanguage
        };
        tenantService.updateCurrentTenant(tenantId, data)
            .then((res: guestTenant) => {
                setSelectedTenant(res);
            })
            .catch((error: Error) => {
                //TODO: show if errord out.
            });
    }

    const handleCancel = () => {
        setEmail(selectedTenant?.email ?? '');
        setTel(selectedTenant?.tel ?? '');
        setAdress(selectedTenant?.address ?? '');
        setEmailBescherming(selectedTenant?.emailBescherming ?? '');
        setDefaultLanguage(selectedTenant?.defaultLanguage ?? '');
        setAddressOneline(selectedTenant?.oneLineAddress ?? '');
    }

    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
        const target = e.target as HTMLImageElement;
        if (target.src.includes('logos')) {
            setLogoError(true);
        } else {
            setBackgroundError(true);
        }
    };

    const changeDefaultLanguage = (event: SelectChangeEvent) => {
        setDefaultLanguage(event.target.value);
    };

    const textFieldProps = (disabled: boolean = true): TextFieldProps => {
        return {
            disabled,
            size: 'small',
            sx: {
                width: '50%',
                backgroundColor: disabled ? 'unset' : 'white'
            }
        }
    }

    return (
        <Box>
            <Typography variant='h4'>
                {t('settings.zoneInfo.title')}
            </Typography>
            <Box>
                {t('settings.zoneInfo.description')}
            </Box>
            {loading && (
                <Box sx={{ marginTop: '1rem' }}>
                    <LinearProgress/>
                </Box>
            )}
            {!loading && (
                <Box sx={{ marginTop: '1rem' }}>
                    <Grid container spacing={2} sx={ Styles.infoWrapper(theme) }>
                        <Grid item xs={12} md={3}>
                            <Box>
                                {t('settings.zoneInfo.name')}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <TextField value={selectedTenant?.displayName} {...textFieldProps()} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Box>
                                {t('settings.zoneInfo.number')}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <TextField value={selectedTenant?.zoneNum} {...textFieldProps()} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Box>
                                {t('settings.zoneInfo.defaultLanguage')}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <FormControl size="small">
                                <Select
                                    sx={{ width: '150px', backgroundColor: 'white' }}
                                    displayEmpty
                                    id="processor-select"
                                    value={defaultLanguage}
                                    onChange={changeDefaultLanguage}
                                >
                                    {availableLanguages.map((name) => (
                                        <MenuItem key={name.value} value={`${name.value}`}>
                                            {name.displayName}
                                        </MenuItem>
                                    ))}
                                </Select>
                                <FormHelperText>
                                    {t('settings.zoneInfo.defaultLanguageHelper')}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2} sx={ Styles.infoWrapper(theme) }>
                        <Grid item xs={12} md={3}>
                            <Box>
                                {t('settings.zoneInfo.email')}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <TextField value={email} onChange={(e) => setEmail(e.target.value)} {...textFieldProps(checkAuth(['TenantAdministrator']) ? false : true)}/>
                            <FormControl>
                                <FormHelperText>
                                    {t('settings.zoneInfo.emailHelper')}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Box>
                                {t('settings.zoneInfo.tel')}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <TextField value={tel} onChange={(e) => setTel(e.target.value)} {...textFieldProps(false)} />
                            <FormControl>
                                <FormHelperText>
                                    {t('settings.zoneInfo.telHelper')}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Box>
                                {t('settings.zoneInfo.address-single')}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <TextField value={addressOnleline} onChange={(e) => setAddressOneline(e.target.value)} {...textFieldProps(false)} />
                            <FormControl>
                                <FormHelperText>
                                    {t('settings.zoneInfo.address-singleHelper')}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Box>
                                {t('settings.zoneInfo.address-multi')}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <TextField multiline value={adress} onChange={(e) => setAdress(e.target.value)} {...textFieldProps(false)} />
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Box>
                                {t('settings.zoneInfo.emailBescherming')}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            <TextField value={emailBescherming} onChange={(e) => setEmailBescherming(e.target.value)} {...textFieldProps(false)} />
                            <FormControl>
                                <FormHelperText>
                                    {t('settings.zoneInfo.emailBeschermingHelper')}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                        </Grid>
                        
                        <Grid container spacing={2} sx={ Styles.infoWrapper(theme) }>
                        <Grid item xs={12} md={3}>
                            <Box>
                                {t('settings.zoneInfo.logo')}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            {!logoError ? (
                                <img onError={handleImageError} src={`/logos/${selectedTenant?.name}.svg`} height={90} alt={'Zone logo'} />
                            ) : (
                                <ImageNotSupportedIcon fontSize='large' />
                            )}
                            <FormControl>
                                <FormHelperText>
                                    {t('settings.zoneInfo.logoHelper')}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} md={3}>
                            <Box>
                                {t('settings.zoneInfo.background')}
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={9}>
                            {!backgroundError ? (
                                <img onError={handleImageError} src={`/background/${selectedTenant?.name}.png`} height={90} alt={'Zone logo'} />
                            ) : (
                                <ImageNotSupportedIcon fontSize='large' />
                            )}
                            <FormControl>
                                <FormHelperText>
                                    {t('settings.zoneInfo.backgroundHelper')}
                                </FormHelperText>
                            </FormControl>
                        </Grid>
                    </Grid>

                    
                    {(
                        selectedTenant?.email !== email ||
                        selectedTenant?.tel !== tel ||
                        selectedTenant?.address !== adress ||
                        selectedTenant?.emailBescherming !== emailBescherming ||
                        selectedTenant?.defaultLanguage !== defaultLanguage ||
                        selectedTenant?.oneLineAddress !== addressOnleline
                    ) &&
                        <Grid item xs={12} md={12} sx={{
                            display: 'flex',
                            justifyContent: 'center'
                        }}>
                            <Button
                                variant='contained'
                                color='cancel'
                                size='large'
                                onClick={handleCancel}
                                sx={{
                                    marginRight: '1rem'
                                }}
                            >
                                {t('buttons.cancel')}
                            </Button>
                            <Button
                                variant='contained'
                                color='success'
                                size='large'
                                onClick={handleUpdate}
                                sx={{
                                    marginLeft: '1rem'
                                }}
                            >
                                {t('buttons.save')}
                            </Button>
                        </Grid>
                    }
                </Box>
            )}
        </Box>
    );
}

export default ZoneTab;